import PropTypes from 'prop-types';
import React, { useCallback, useContext } from 'react';
import styled, { css } from 'styled-components';

import AnswerPerformanceView from 'models/location/answer_performance_view';
import connect from 'components/lib/connect';
import GuidePerformanceView from 'models/location/guide_performance_view';
import Inbox from 'models/location/inbox';
import KbAdmin from 'models/location/kb_admin';
import Link from 'components/common/link';
import LinkOutStroke from 'components/common/icons/stroke/link-out-stroke';
import Liveboards from 'models/location/liveboards';
import NavigateTo from 'actions/current_location/navigate_to';
import Reports from 'models/location/reports';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import { getLocationUrl } from 'scripts/adapters/routes/location_url';
import { HotkeysShownContext } from 'components/contexts/hotkeys';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export function NavigationMenu({ hasGuideCollections, isSidekickEnabled, onClose }) {
  const isFeatureEnabled = useIsFeatureEnabled();
  const { dispatchHotkeysShown } = useContext(HotkeysShownContext);
  const showHotkeys = useCallback(() => dispatchHotkeysShown('on'), [dispatchHotkeysShown]);

  function renderSidekickMenuOptions() {
    if (!isSidekickEnabled || !satisfiesFeatures('sidekickConversationManagement')) {
      return null;
    }

    return (
      <>
        <StyledMenuItem data-aid="sidekick-conversations">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/communication-sessions"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/communication-sessions');
            }}
          >
            Sidekick Conversations
          </StyledLink>
        </StyledMenuItem>
        <StyledMenuItem data-aid="sidekick-guides">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/guides"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/guides');
            }}
          >
            Guides
          </StyledLink>
        </StyledMenuItem>
      </>
    );
  }

  // satisfies at least one feature
  const satisfiesFeatures = features => {
    if (typeof features === 'string') features = [features];
    return features.some(feature => isFeatureEnabled(feature));
  };

  function renderJourneysLink() {
    if (!isSidekickEnabled) {
      return null;
    }

    if (!satisfiesFeatures(['viewGuidePerformance', 'viewAnswerPerformance', 'answers:admin', 'answers:read'])) {
      return null;
    }

    const opts =
      hasGuideCollections && satisfiesFeatures('viewGuidePerformance')
        ? { dataAid: 'guide-performance', locationModel: GuidePerformanceView }
        : { dataAid: 'answer-performance', locationModel: AnswerPerformanceView };

    return <MenuOption data-aid={opts.dataAid} locationModel={opts.locationModel} onClick={onClose} title="Journeys" />;
  }

  return (
    <div className={`navigationMenu navigationMenuV2`}>
      <StyledLine />
      {satisfiesFeatures(['conversation:read']) && (
        <MenuOption locationModel={Inbox} locationPath="" onClick={onClose} title="Home" />
      )}
      {satisfiesFeatures(['answers:admin', 'answers:read']) && (
        <MenuOption locationModel={KbAdmin} onClick={onClose} title="Answers" />
      )}

      {renderJourneysLink()}

      {renderSidekickMenuOptions()}

      {satisfiesFeatures('reports:read') && <MenuOption locationModel={Reports} onClick={onClose} title="Reports" />}
      {satisfiesFeatures('viewLiveboards') && (
        <MenuOption locationModel={Liveboards} onClick={onClose} title="Liveboards" />
      )}
      <StyledLine />
      {satisfiesFeatures(['adminSettings', 'devManagement', 'debuggingView', 'answers:admin', 'topics:admin']) && (
        <StyledMenuItem data-aid="settings-menu">
          <StyledLink
            className="navigationMenu-option"
            href="/admin/home"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/admin/home');
            }}
          >
            Settings
          </StyledLink>
        </StyledMenuItem>
      )}
      <StyledLine />
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-new-features"
          href="https://help.gladly.com/docs/whats-new"
          tabIndex="-1"
          target="release-notes"
        >
          What's new in Gladly
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-help-link"
          href="https://help.gladly.com"
          tabIndex="-1"
          target="help"
        >
          Help Docs
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      {satisfiesFeatures('contactSupport') && (
        <StyledMenuItem data-aid="contact-support-menu">
          <StyledExternalLink
            className="navigationMenu-option"
            data-aid="navigationMenu-help-link"
            href="/support.html"
            tabIndex="-1"
            target="support"
          >
            Contact Support
            <StyledLinkOutStroke />
          </StyledExternalLink>
        </StyledMenuItem>
      )}
      <StyledLine />
      <StyledMenuItem>
        <StyledLink
          className="navigationMenu-option"
          data-aid="navigationMenu-shortcuts-link"
          onClick={showHotkeys}
          tabIndex="-1"
        >
          Keyboard Shortcuts
        </StyledLink>
      </StyledMenuItem>
      <PrivacyPolicySection>
        <PrivacyPolicyLink
          data-aid="navigationMenu-privacy-policy-link"
          href="https://www.gladly.com/privacy-policy/"
          rel="noopener noreferrer"
          tabIndex="-1"
          target="_blank"
        >
          Privacy Policy
        </PrivacyPolicyLink>
      </PrivacyPolicySection>
    </div>
  );
}

NavigationMenu.propTypes = {
  hasGuideCollections: PropTypes.bool.isRequired,
  isSidekickEnabled: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const NavigationMenuContainer = connect(mapStateToProps)(NavigationMenu);
export default NavigationMenuContainer;

function mapStateToProps({ getProvider }, { onClose }) {
  const organization = getProvider('organization').get();
  return {
    onClose,
    hasGuideCollections: getProvider('guideCollections').findAll().length > 0,
    isSidekickEnabled: !!organization?.isSidekickEnabled,
  };
}

export const MenuOption = ({ locationModel, onClick, title }) => {
  const executeAction = useExecuteAction();
  const handleClick = useCallback(
    evt => {
      evt.preventDefault();
      executeAction(NavigateTo, locationModel.create());
      onClick();
    },
    [executeAction, locationModel, onClick]
  );

  const menuPath = getLocationUrl(locationModel.create());
  const currentLocation = window.location.pathname.split('/')[1];
  const isActiveItem = menuPath.split('/')[1] === currentLocation;
  return (
    <StyledMenuItem isActive={isActiveItem}>
      <StyledLink className="navigationMenu-option" href={menuPath} onClick={handleClick}>
        {title}
      </StyledLink>
    </StyledMenuItem>
  );
};

MenuOption.propTypes = {
  locationModel: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

const ActiveItem = css`
  background-color: ${p => p.theme.colors.gray200};
  font-weight: bold;
  &:before {
    background-color: ${p => p.theme.colors.green500};
    bottom: 0;
    content: '';
    left: 0;
    margin: auto;
    position: absolute;
    top: 0;
    width: 4px;
  }
`;

const StyledLinkOutStroke = styled(LinkOutStroke)`
  height: 14px;
  width: 14px;
  margin-left: 4px;
`;

export const StyledLink = styled(Link)`
  color: ${p => p.theme.colors.black};
  align-items: center;
  display: flex;
  height: 32px;
  padding: 6px 24px;
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.black};
    text-decoration: none;
  }
`;

const StyledLine = styled.div`
  height: 1px;
  background-color: ${p => p.theme.colors.gray300};
  margin: 2px 0;

  & + & {
    display: none;
  }
`;

const StyledMenuItem = styled.div`
  cursor: pointer;
  padding: 0;
  position: relative;
  &:hover {
    background-color: ${p => p.theme.colors.green100};
  }
  ${p => p.isActive && ActiveItem}
`;

export const StyledExternalLink = styled.a`
  color: ${p => p.theme.colors.black};
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.green400};
    text-decoration: none;
  }
`;

const PrivacyPolicySection = styled.div`
  bottom: 10px;
  left: 0;
  width: 100%;
  padding: 8px 0;
  position: absolute;
`;

const PrivacyPolicyLink = styled.a`
  color: ${p => p.theme.colors.gray800};
  display: block;
  width: 100%;
  height: 32px;
  padding: 6px 24px;
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.gray800};
    text-decoration: none;
    background-color: ${p => p.theme.colors.green100};
  }
`;
